import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { CircleHelp, Edit2, Plus, TriangleAlert, Trash2 } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import Select from "react-select";
import { ReactComponent as RefreshIcon } from "../../../assets/icons/refresh.svg";
import ConfirmationPopup from "../CommonComponents/PopUpCards/ConfirmationPopup";
import { ReactComponent as ConfirmationIcon } from "../../../assets/icons/Confirmation.svg";
import DynamicTable from "../CommonComponents/Table/DynamicTable";
import CommonModal from "../CommonComponents/Modal/CommonModal";
import { commonStyles } from "../../../assets/MUIStyles/commonStyles";
import { dashboardStyles } from "../../../assets/MUIStyles/dashboard";
import { poolStyles } from "../../../assets/MUIStyles/projectPools";
import SummaryCard from "../Dashboard/SummaryCard";
import BaseButton from "../Common/Buttons/BaseButton";
import { GreenSwitch } from "../Common/GreenSwitch";
import { getProjectsAction } from "../Projects/Store/projects.action";
import {
  createProjectPoolAction,
  deleteProjectPoolAction,
  getProjectPoolsAction,
  updateProjectPoolAction,
} from "./Store/projectPools.action";
import "./ProjectPools.css";

const POOL_ACCESS_KEY = "cv-pools-9f4a2";

const emptyForm = {
  name: "",
  share_percent: "",
  projectIds: [] as string[],
  gatewayIds: [] as string[],
  status: "active",
};

// two names plus a +N pill, full list on hover
const ChipList = ({ items }) => {
  if (!items?.length) return <span className="font-12-500-lightgrey">--</span>;
  const shown = items.slice(0, 2);
  const rest = items.length - shown.length;
  return (
    <Box sx={poolStyles.chipRow} title={items.join(", ")}>
      {shown.map((item) => (
        <Box component="span" sx={poolStyles.chip} key={item}>
          {item}
        </Box>
      ))}
      {rest > 0 ? (
        <Box component="span" sx={poolStyles.chip}>
          +{rest}
        </Box>
      ) : null}
    </Box>
  );
};

function ProjectPools() {
  const dispatch = useDispatch();
  const brandColor = useTheme().custom.brandColor;
  const location = useLocation();
  const locSearch = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  // ?poolAccess=cv-pools-9f4a2 unlocks the status toggle and force delete
  const adminMode = locSearch.get("poolAccess") === POOL_ACCESS_KEY;
  const reducer: any = useSelector((state) => state);
  const {
    ProjectReducer: { projectList },
    AccountReducer: { constantData },
  } = reducer;

  const [loading, setLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [pools, setPools] = useState<any[]>([]);
  const [accountGateways, setAccountGateways] = useState<any[]>([]);
  const [totalConcurrency, setTotalConcurrency] = useState(0);
  const [sharesSum, setSharesSum] = useState(0);
  const [modal, setModal] = useState("");
  const [spin, setSpin] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState("");
  const [deleteId, setDeleteId] = useState("");
  const [togglingId, setTogglingId] = useState("");

  const pausedCount = useMemo(
    () => pools.filter((pool) => (pool.status || "active") !== "active").length,
    [pools]
  );

  const queuedTotal = useMemo(
    () => pools.reduce((acc, pool) => acc + (Number(pool.queue_count) || 0), 0),
    [pools]
  );

  const projectOptions = useMemo(() => {
    return (projectList || []).map((project) => ({
      value: project._id || project.id,
      label: project.projectName || project.name || project._id,
    }));
  }, [projectList]);

  const usedProjectIds = useMemo(() => {
    const used = new Set();
    pools.forEach((pool) => {
      if (editingId && pool._id === editingId) return;
      (pool.projectIds || []).forEach((id) => used.add(id));
    });
    return used;
  }, [pools, editingId]);

  const selectableProjects = projectOptions.filter(
    (opt) => !usedProjectIds.has(opt.value)
  );

  const loadPools = async (isRefresh = false) => {
    if (isRefresh) setRefreshLoading(true);
    else setLoading(true);
    try {
      const res: any = await dispatch(getProjectPoolsAction() as any);
      setPools(res?.pools || []);
      setAccountGateways(res?.gateways || []);
      setTotalConcurrency(res?.total_concurrency || 0);
      setSharesSum(res?.shares_sum || 0);
    } finally {
      setLoading(false);
      setRefreshLoading(false);
    }
  };

  useEffect(() => {
    dispatch(getProjectsAction());
    loadPools();
  }, []);

  const openCreate = () => {
    setEditingId("");
    setForm(emptyForm);
    setModal("edit");
  };

  const openEdit = (row) => {
    setEditingId(row._id);
    setForm({
      name: row.name || "",
      share_percent: String(row.share_percent ?? ""),
      projectIds: row.projectIds || [],
      gatewayIds: row.gatewayIds || [],
      status: row.status || "active",
    });
    setModal("edit");
  };

  const previewSum = () => {
    const current = Number(form.share_percent) || 0;
    const others = pools.reduce((acc, pool) => {
      if (editingId && pool._id === editingId) return acc;
      return acc + (Number(pool.share_percent) || 0);
    }, 0);
    return others + current;
  };

  const shareValue = Number(form.share_percent);
  const formValid =
    Boolean(form.name.trim()) &&
    form.projectIds.length > 0 &&
    form.gatewayIds.length > 0 &&
    shareValue > 0 &&
    shareValue <= 100;

  // concurrency and pooled_ai come from the pool API, names from system constants
  const gatewayMeta = useMemo(() => {
    const map = new Map();
    (accountGateways || []).forEach((gw) => map.set(gw.value, gw));
    return map;
  }, [accountGateways]);

  const gatewayName = (id) => {
    const found = (constantData?.gateways || []).find(
      (gw) => gw?.value === id
    );
    return found?.name || id;
  };

  const gatewayOptions = useMemo(() => {
    const fromConstants = (constantData?.gateways || []).map((gw) => {
      const meta = gatewayMeta.get(gw.value);
      const pooled = Boolean(meta?.pooled_ai);
      const parts = [gw.name || gw.value];
      if (meta) {
        parts.push(String(meta.concurrency ?? 0));
        if (!pooled) parts.push("Pooled AI off");
      } else {
        parts.push("not on this account");
      }
      return {
        value: gw.value,
        pooled_ai: pooled,
        onAccount: Boolean(meta),
        label: parts.join(" · "),
      };
    });
    const known = new Set(fromConstants.map((opt) => opt.value));
    const extras = (form.gatewayIds || [])
      .filter((id) => !known.has(id))
      .map((id) => ({
        value: id,
        pooled_ai: Boolean(gatewayMeta.get(id)?.pooled_ai),
        onAccount: gatewayMeta.has(id),
        label: `${id} · not in gateway constants`,
      }));
    return fromConstants
      .sort((a, b) => {
        if (a.pooled_ai !== b.pooled_ai) return a.pooled_ai ? -1 : 1;
        if (a.onAccount !== b.onAccount) return a.onAccount ? -1 : 1;
        return a.label.localeCompare(b.label);
      })
      .concat(extras);
  }, [constantData, gatewayMeta, form.gatewayIds]);

  const handleSave = async () => {
    const payload = {
      name: form.name.trim(),
      share_percent: Number(form.share_percent),
      projectIds: form.projectIds,
      gatewayIds: form.gatewayIds,
      status: form.status || "active",
    };
    if (
      !payload.name ||
      !payload.projectIds.length ||
      !payload.gatewayIds.length ||
      payload.share_percent <= 0 ||
      payload.share_percent > 100
    ) {
      return;
    }
    setSpin(true);
    try {
      if (editingId) {
        await dispatch(updateProjectPoolAction(editingId, payload));
      } else {
        await dispatch(createProjectPoolAction(payload));
      }
      setModal("");
      setEditingId("");
      setForm(emptyForm);
      await loadPools();
    } finally {
      setSpin(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId || !adminMode) return;
    setSpin(true);
    try {
      await dispatch(deleteProjectPoolAction(deleteId, true) as any);
      setDeleteId("");
      await loadPools();
    } finally {
      setSpin(false);
    }
  };

  const handleStatusToggle = async (row, checked) => {
    if (!adminMode || !row?._id) return;
    const status = checked ? "active" : "paused";
    setTogglingId(row._id);
    try {
      await dispatch(updateProjectPoolAction(row._id, { status }) as any);
      await loadPools(true);
    } finally {
      setTogglingId("");
    }
  };

  const closeForm = () => {
    setModal("");
    setEditingId("");
    setForm(emptyForm);
  };

  const shareWarning = (sum) => {
    if (sum === 100) return "";
    return `Shares add to ${sum}%. A gateway only splits the pools it belongs to, using these percents as weights. The sum does not have to be 100%.`;
  };

  const projectLabel = (id) => {
    const found = projectOptions.find((opt) => opt.value === id);
    return found?.label || id;
  };

  return (
    <Box className="project-pools-page">
      <Box className="flex-between project-pools-header">
        <Box>
          <Typography className="font-16-700-grey">Project Pools</Typography>
          <Typography className="font-12-lightgrey-2 pool-page-subtitle">
            Split pooled-AI dialing capacity across groups of projects.
          </Typography>
        </Box>
        <Box className="flex-align-center" sx={{ gap: 1 }}>
          <IconButton
            title="Refresh"
            onClick={() => loadPools(true)}
            disabled={loading || refreshLoading}
            sx={{
              ...commonStyles.refreshButtonStyle,
              ...dashboardStyles.refreshAnimation(refreshLoading),
            }}
          >
            <RefreshIcon className="refresh-icon" height={14} width={14} />
          </IconButton>
          <BaseButton
            variant="outlined"
            title="How it works"
            startIcon={<CircleHelp size={14} />}
            handleClick={() => setModal("guide")}
          />
          <BaseButton
            variant="contained"
            title="Add pool"
            startIcon={<Plus size={14} />}
            handleClick={openCreate}
          />
        </Box>
      </Box>

      <Box sx={commonStyles.summaryCardContainer}>
        <SummaryCard
          title="Pools"
          value={`${pools.length}${pausedCount ? ` · ${pausedCount} paused` : ""}`}
          circleColor="linear-gradient(237.96deg, rgba(150, 255, 143, 0.8) -12.91%, rgba(87, 209, 78, 0.8) 100.74%)"
        />
        <SummaryCard
          title="Share sum"
          value={`${sharesSum}%`}
          circleColor="linear-gradient(122.19deg, rgba(255, 143, 224, 0.8) 34.92%, rgba(242, 93, 203, 0.8) 100.02%)"
        />
        <SummaryCard
          title="Calls queued"
          value={queuedTotal}
          circleColor="linear-gradient(180deg, rgba(143, 196, 255, 0.8) 0%, rgba(66, 133, 226, 0.8) 100%)"
        />
      </Box>

      {pools.length > 0 && sharesSum !== 100 ? (
        <Box sx={commonStyles.alertWarning}>
          <Typography className="font-12-lightgrey">
            {shareWarning(sharesSum)}
          </Typography>
        </Box>
      ) : null}

      {adminMode ? (
        <Box sx={poolStyles.forceDeleteAlert}>
          <TriangleAlert size={16} />
          <Box>
            <Typography className="font-13-500-grey">
              Admin mode is on
            </Typography>
            <Typography className="font-12-400-lightgrey">
              Status can be toggled and pools can be deleted. Delete goes
              through even if calls are still in that pool&apos;s queue, and
              those queued calls can be stranded.
            </Typography>
          </Box>
        </Box>
      ) : null}

      <DynamicTable
        loading={loading}
        data={pools}
        rowCount={pools.length}
        pagination={false}
        noDataAvailableMessage="No pools yet. Add one to split pooled-AI capacity across projects."
        columns={[
          {
            label: "Name",
            render: (row) => (
              <Typography className="font-12-600-grey">
                {row.name || "--"}
              </Typography>
            ),
          },
          {
            label: "Share",
            render: (row) => (
              <Box sx={poolStyles.shareCell}>
                <Typography className="font-12-mediumgrey">
                  {row.share_percent ?? 0}%
                </Typography>
                <Box sx={poolStyles.shareBar}>
                  <Box
                    sx={poolStyles.shareBarFill(
                      Math.min(Math.max(Number(row.share_percent) || 0, 0), 100),
                      brandColor
                    )}
                  />
                </Box>
              </Box>
            ),
          },
          {
            label: "Queue",
            render: (row) => (
              <Typography
                className={
                  row.queue_count ? "font-12-mediumgrey" : "font-12-500-lightgrey"
                }
              >
                {row.queue_count ?? 0}
              </Typography>
            ),
          },
          {
            label: "Gateways",
            render: (row) => (
              <ChipList items={(row.gatewayIds || []).map(gatewayName)} />
            ),
          },
          {
            label: "Projects",
            render: (row) => (
              <ChipList items={(row.projectIds || []).map(projectLabel)} />
            ),
          },
          {
            label: "Status",
            render: (row) => {
              const active = (row.status || "active") === "active";
              return (
                <Box
                  sx={poolStyles.statusCell}
                  title="Paused pools are skipped when dialing"
                >
                  {adminMode ? (
                    <GreenSwitch
                      checked={active}
                      disabled={togglingId === row._id}
                      onChange={(event) =>
                        handleStatusToggle(row, event.target.checked)
                      }
                    />
                  ) : null}
                  <Typography
                    className={
                      active ? "font-12-mediumgrey" : "font-12-500-lightgrey"
                    }
                  >
                    {active ? "Active" : "Paused"}
                  </Typography>
                </Box>
              );
            },
          },
          {
            label: "Actions",
            isAction: true,
            render: (row) => (
              <Box className="flex-xy-center">
                <IconButton title="Edit pool" onClick={() => openEdit(row)}>
                  <Edit2 size={15} />
                </IconButton>
                {adminMode ? (
                  <IconButton
                    className="pool-delete-btn"
                    title="Force delete pool"
                    onClick={() => setDeleteId(row._id)}
                  >
                    <Trash2 size={15} />
                  </IconButton>
                ) : null}
              </Box>
            ),
          },
        ]}
      />


      <CommonModal
        show={modal === "edit"}
        heading={editingId ? "Edit pool" : "Create pool"}
        size={640}
        customClassName="project-pool-modal"
        toggle={closeForm}
        handleSave={handleSave}
        saveButtonText="Save"
        spin={spin}
        disableSaveButton={!formValid}
        showNextButton
        nextButtonText="Cancel"
        handleNext={closeForm}
        disableNextButton={spin}
        body={
          <Box>
            <Box sx={poolStyles.formStats}>
              <Box>
                <Typography className="font-12-500-lightgrey">
                  Pooled-AI concurrency
                </Typography>
                <Typography className="font-13-600-grey">
                  {totalConcurrency}
                </Typography>
              </Box>
              <Box>
                <Typography className="font-12-500-lightgrey">
                  Share sum after save
                </Typography>
                <Typography className="font-13-600-grey">
                  {previewSum()}%
                </Typography>
              </Box>
            </Box>

            <Box sx={poolStyles.formRow}>
              <Box sx={poolStyles.field}>
                <label className="font-14-grey">
                  Name<span className="pool-req">*</span>
                </label>
                <input
                  className="common-ip"
                  autoFocus
                  placeholder="e.g. Lender 1 Bucket 1"
                  value={form.name}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                />
              </Box>
              <Box sx={poolStyles.field}>
                <label className="font-14-grey">
                  Share percent<span className="pool-req">*</span>
                </label>
                <Box sx={poolStyles.inputSuffix}>
                  <input
                    className="common-ip"
                    type="number"
                    min="0.01"
                    max="100"
                    step="0.01"
                    placeholder="50"
                    value={form.share_percent}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        share_percent: e.target.value,
                      }))
                    }
                  />
                  <span>%</span>
                </Box>
                {form.share_percent !== "" &&
                (shareValue <= 0 || shareValue > 100) ? (
                  <Typography className="pool-field-error">
                    Enter a percent above 0 and up to 100.
                  </Typography>
                ) : null}
              </Box>
            </Box>

            {previewSum() !== 100 ? (
              <Box sx={{ ...commonStyles.alertWarning, mb: 2 }}>
                <Typography className="font-12-lightgrey">
                  Shares add to {previewSum()}%, which is allowed. A gateway
                  only splits the pools it belongs to, using these percents as
                  weights.
                </Typography>
              </Box>
            ) : null}

            <Box sx={poolStyles.field}>
              <label className="font-14-grey">
                Gateways<span className="pool-req">*</span>
              </label>
              <Select
                isMulti
                options={gatewayOptions}
                value={gatewayOptions.filter((opt) =>
                  form.gatewayIds.includes(opt.value)
                )}
                onChange={(selected) =>
                  setForm((prev) => ({
                    ...prev,
                    gatewayIds: (selected || []).map((opt) => opt.value),
                  }))
                }
                placeholder="Select gateways"
                styles={commonStyles.customSelect}
                menuPortalTarget={document.body}
                maxMenuHeight={180}
                closeMenuOnSelect={false}
              />
              <Typography className="font-12-400-lightgrey pool-field-hint mt-1">
                Only gateways with Pooled AI on add capacity; the rest show as
                Pooled AI off. A gateway can sit in more than one pool.
              </Typography>
            </Box>

            <Box sx={poolStyles.field}>
              <label className="font-14-grey">
                Projects<span className="pool-req">*</span>
              </label>
              <Select
                isMulti
                options={selectableProjects}
                value={selectableProjects
                  .concat(
                    projectOptions.filter((opt) =>
                      form.projectIds.includes(opt.value)
                    )
                  )
                  .filter(
                    (opt, idx, arr) =>
                      form.projectIds.includes(opt.value) &&
                      arr.findIndex((o) => o.value === opt.value) === idx
                  )}
                onChange={(selected) =>
                  setForm((prev) => ({
                    ...prev,
                    projectIds: (selected || []).map((opt) => opt.value),
                  }))
                }
                placeholder="Select projects"
                styles={commonStyles.customSelect}
                menuPortalTarget={document.body}
                maxMenuHeight={180}
                closeMenuOnSelect={false}
              />
              <Typography className="font-12-400-lightgrey pool-field-hint mt-1">
                A project can belong to only one pool, so projects already in
                another pool are not listed.
              </Typography>
            </Box>
          </Box>
        }
      />

      <CommonModal
        show={modal === "guide"}
        heading="How project pools work"
        size={880}
        customClassName="project-pool-modal"
        maxModalHeight="72vh"
        toggle={() => setModal("")}
        handleSave={() => setModal("")}
        saveButtonText="Got it"
        body={<Box className="pool-guide-body">
              <Typography className="font-12-400-lightgrey pool-guide-p">
                A project pool is a named group of projects that share one slice of
                on-prem dialing capacity. Campaigns in those projects still enqueue
                as usual. When a pooled-AI gateway asks for the next batch of calls,
                this page decides how many slots each pool gets.
              </Typography>

              <Typography className="font-13-600-grey pool-guide-h">
                1. Only pooled-AI gateways count
              </Typography>
              <Typography className="font-12-400-lightgrey pool-guide-p">
                Turn on <strong>Pooled AI</strong> for a gateway under Account
                Settings → Gateway configuration. Capacity is the sum of{" "}
                <code>concurrency</code> on gateways where{" "}
                <code>pooled_ai = true</code>. Other gateways keep their own queues
                and are ignored here. If no gateway is pooled, concurrency is 0 and
                Slots stays 0.
              </Typography>
              <svg
                className="pool-diagram"
                viewBox="0 0 760 150"
                role="img"
                aria-label="Only pooled AI gateways contribute concurrency"
              >
                <rect className="pool-diagram-box-on" x="12" y="28" width="150" height="70" rx="6" />
                <text className="pool-diagram-title" x="87" y="58">
                  Gateway A
                </text>
                <text className="pool-diagram-sub" x="87" y="78">
                  pooled_ai yes · 40
                </text>
                <rect className="pool-diagram-box-off" x="186" y="28" width="150" height="70" rx="6" />
                <text className="pool-diagram-title" x="261" y="58">
                  Gateway B
                </text>
                <text className="pool-diagram-sub" x="261" y="78">
                  pooled_ai no · 80
                </text>
                <rect className="pool-diagram-box-on" x="360" y="28" width="150" height="70" rx="6" />
                <text className="pool-diagram-title" x="435" y="58">
                  Gateway C
                </text>
                <text className="pool-diagram-sub" x="435" y="78">
                  pooled_ai yes · 20
                </text>
                <path className="pool-diagram-arrow" d="M520 63 L568 63" />
                <polygon className="pool-diagram-arrow-head" points="568,58 580,63 568,68" />
                <rect className="pool-diagram-box-total" x="588" y="28" width="156" height="70" rx="6" />
                <text className="pool-diagram-title" x="666" y="58">
                  Pool capacity
                </text>
                <text className="pool-diagram-sub" x="666" y="78">
                  40 + 20 = 60
                </text>
              </svg>

              <Typography className="font-13-600-grey pool-guide-h">
                2. Share percent is a slice of that capacity
              </Typography>
              <Typography className="font-12-400-lightgrey pool-guide-p">
                Slots on each row is{" "}
                <code>floor(this pool&apos;s gateway concurrency × share% / 100)</code>
                . Only gateways listed on that pool, and only while Pooled AI is
                on, add to that number. A gateway in two pools is counted in both.
                A project can belong to only one pool. Paused pools are skipped
                when dialing.
              </Typography>
              <svg
                className="pool-diagram"
                viewBox="0 0 980 100"
                role="img"
                aria-label="Share percents split pooled capacity"
              >
                <rect className="pool-diagram-share-a" x="12" y="16" width="478" height="68" rx="4" />
                <text className="pool-diagram-on-text" x="251" y="44">
                  Lender 1 Bucket 1
                </text>
                <text className="pool-diagram-sub" x="251" y="64">
                  50% → 30
                </text>
                <rect className="pool-diagram-share-b" x="494" y="16" width="286" height="68" rx="4" />
                <text className="pool-diagram-on-text" x="637" y="44">
                  Lender 1 Bucket 2
                </text>
                <text className="pool-diagram-sub" x="637" y="64">
                  30% → 18
                </text>
                <rect className="pool-diagram-share-c" x="784" y="16" width="184" height="68" rx="4" />
                <text className="pool-diagram-on-text" x="876" y="44">
                  Lender 2 Bucket 1
                </text>
                <text className="pool-diagram-sub" x="876" y="64">
                  20% → 12
                </text>
              </svg>

              <Typography className="font-13-600-grey pool-guide-h">
                3. How a gateway pulls the next calls
              </Typography>
              <Typography className="font-12-400-lightgrey pool-guide-p">
                An on-prem pooled-AI gateway requests N slots. Only active pools
                that list that gateway are in the split. ISDN 5 in only Pool A
                takes the whole batch from Pool A. ISDN 6 in Pool A (70%) and Pool
                B (30%) splits about 70/30. Inside a pool, projects are tried in
                the order you saved them. A pooled gateway that is in no pool gets
                nothing. Queue on this page is how many calls are waiting for that
                pool.
              </Typography>
              <svg
                className="pool-diagram"
                viewBox="0 0 760 180"
                role="img"
                aria-label="Gateway asks for slots then pools split them"
              >
                <rect className="pool-diagram-box-on" x="12" y="50" width="170" height="80" rx="6" />
                <text className="pool-diagram-title" x="97" y="82">
                  Pooled gateway
                </text>
                <text className="pool-diagram-sub" x="97" y="102">
                  Give me 10 calls
                </text>
                <path className="pool-diagram-arrow" d="M190 90 L248 90" />
                <polygon className="pool-diagram-arrow-head" points="248,85 260,90 248,95" />
                <rect className="pool-diagram-box-total" x="268" y="50" width="170" height="80" rx="6" />
                <text className="pool-diagram-title" x="353" y="82">
                  Split by shares
                </text>
                <text className="pool-diagram-sub" x="353" y="102">
                  50 / 30 / 20 of 10
                </text>
                <path className="pool-diagram-arrow" d="M446 70 L500 40" />
                <path className="pool-diagram-arrow" d="M446 90 L500 90" />
                <path className="pool-diagram-arrow" d="M446 110 L500 140" />
                <rect className="pool-diagram-share-a" x="508" y="18" width="236" height="36" rx="4" />
                <text className="pool-diagram-on-text" x="626" y="41">
                  Lender 1 Bucket 1 → 5
                </text>
                <rect className="pool-diagram-share-b" x="508" y="72" width="236" height="36" rx="4" />
                <text className="pool-diagram-on-text" x="626" y="95">
                  Lender 1 Bucket 2 → 3
                </text>
                <rect className="pool-diagram-share-c" x="508" y="126" width="236" height="36" rx="4" />
                <text className="pool-diagram-on-text" x="626" y="149">
                  Lender 2 Bucket 1 → 2
                </text>
              </svg>

              <Typography className="font-13-600-grey pool-guide-h">
                4. Share sum does not have to be 100%
              </Typography>
              <Typography className="font-12-400-lightgrey pool-guide-p">
                Saving is allowed if the percents add to less or more than 100%.
                Dequeue uses the percents as weights among the pools a gateway
                belongs to (60 and 50 behave like 60:50). Slots uses percent ÷ 100
                of that pool&apos;s own gateways, so the Slots column can add up
                to more than one gateway&apos;s concurrency when a trunk is shared.
                That is a warning, not a blocker.
              </Typography>
              <Typography className="font-12-400-lightgrey pool-guide-p">
                You cannot delete a pool that still has queued calls. Refresh after
                toggling Pooled AI on a gateway so concurrency updates.
              </Typography>
        </Box>}
      />

      <ConfirmationPopup
        show={Boolean(deleteId)}
        Icon={ConfirmationIcon}
        heading="Force delete pool"
        message="This pool may still have queued calls. Force delete will remove the pool anyway. Queued calls can be stranded."
        handleSave={handleDelete}
        toggle={() => setDeleteId("")}
        confirmText="Delete"
        spin={spin}
      />
    </Box>
  );
}

export default ProjectPools;
